import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitCompare, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Textarea,
} from '@/components/ui';
import { adminApi, skillsApi } from '@/lib/api';
import { useCurrentUser } from '@/lib/auth/AuthProvider';
import { cn } from '@/lib/cn';

import { DiffViewer } from './DiffViewer';

/**
 * Spec S09 §"Skill editor" — PO writes a new version of the prompt + model
 * assignment + thresholds, runs the sandbox, then promotes.
 *
 * Phase 7 ships a functional editor; the diff viewer + side-by-side sandbox
 * UI from the spec lands as part of the Phase 7.5 polish iteration.
 */
export function SkillEditor() {
  const { skillId } = useParams();
  const user = useCurrentUser();
  const queryClient = useQueryClient();

  const versions = useQuery({
    queryKey: ['studio', 'skills', skillId, 'versions'],
    queryFn: () => skillsApi.listVersions(skillId as string),
    enabled: Boolean(skillId),
  });

  const providers = useQuery({
    queryKey: ['admin', 'llm-providers'],
    queryFn: () => adminApi.llmProviders(),
  });

  const [versionLabel, setVersionLabel] = useState('1.0');
  const [rationale, setRationale] = useState('');
  const [model, setModel] = useState<string>('');
  const [promptMarkdown, setPromptMarkdown] = useState('');
  const [createdDraftId, setCreatedDraftId] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState(false);

  const activeDetail = useQuery({
    queryKey: ['studio', 'skills', skillId, 'active'],
    queryFn: () => skillsApi.getActive(skillId as string),
    enabled: Boolean(skillId),
  });

  const saveDraft = useMutation({
    mutationFn: () =>
      skillsApi.createDraft({
        skill_id: skillId as string,
        version_label: versionLabel,
        change_rationale: rationale,
        llm_model: model || undefined,
        prompt_markdown: promptMarkdown,
      }),
    onSuccess: (draft) => {
      setCreatedDraftId(draft.draft_id);
      void queryClient.invalidateQueries({
        queryKey: ['studio', 'skills', skillId, 'versions'],
      });
    },
  });

  const runSandbox = useMutation({
    mutationFn: () => skillsApi.runSandbox(createdDraftId!),
  });

  const promote = useMutation({
    mutationFn: () => skillsApi.promote(createdDraftId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['studio', 'skills'] });
    },
  });

  if (!user?.productOwner) {
    return (
      <div className="p-6">
        <p className="text-body text-slate-600">Product Owner flag required.</p>
      </div>
    );
  }

  const active = versions.data?.find((v) => v.status === 'active');

  return (
    <div className="grid h-full grid-cols-[35%_65%] gap-3 p-3">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-h2 text-brand-900">{skillId}</h2>
            {active && <Badge variant="success">{active.version_label}</Badge>}
          </div>
          <p className="mt-1 text-body text-slate-600">
            Version history + change rationale. The active version is what the
            workers load on every run.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          {versions.isLoading && <p className="text-small text-slate-500">Loading…</p>}
          {versions.data?.length === 0 && (
            <p className="text-small text-slate-500">No versions yet.</p>
          )}
          {versions.data?.map((v) => (
            <div
              key={v.draft_id}
              className="rounded border border-slate-200 px-3 py-2 text-small"
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-brand-900">v{v.version_label}</span>
                <Badge
                  variant={
                    v.status === 'active'
                      ? 'success'
                      : v.status === 'retired'
                        ? 'neutral'
                        : v.status === 'sandbox_run'
                          ? 'warning'
                          : 'info'
                  }
                >
                  {v.status}
                </Badge>
              </div>
              <p className="mt-1 text-slate-600">{v.change_rationale}</p>
              <p className="mt-1 font-mono text-slate-400">
                {new Date(v.created_at).toLocaleString()}
              </p>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-h2 text-brand-900">New draft</h2>
              <p className="mt-1 text-body text-slate-600">
                Required: version label + change rationale + the prompt markdown.
              </p>
            </div>
            <Button
              type="button"
              variant={showDiff ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setShowDiff((v) => !v)}
              disabled={!activeDetail.data}
              title={
                activeDetail.data
                  ? 'Toggle side-by-side diff against the active version'
                  : 'No active version to diff against'
              }
            >
              <GitCompare className="mr-1 h-3.5 w-3.5" aria-hidden />
              {showDiff ? 'Hide diff' : 'Diff vs active'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex h-full flex-col gap-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-h4 text-slate-700">Version label</label>
              <Input value={versionLabel} onChange={(e) => setVersionLabel(e.target.value)} />
            </div>
            <div className="col-span-2">
              <label className="block text-h4 text-slate-700">LLM model</label>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="h-9 w-full rounded border border-slate-300 px-2 text-body"
              >
                <option value="">— inherit default —</option>
                {providers.data?.map((p) => (
                  <option key={p.provider_id} value={p.model_id}>
                    {p.display_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-h4 text-slate-700">Change rationale</label>
            <Textarea
              rows={2}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Why this version exists — link to candidate errors if any"
            />
          </div>

          <div className={cn('flex flex-1 flex-col', showDiff && 'min-h-[20rem]')}>
            <label className="block text-h4 text-slate-700">Prompt (markdown)</label>
            {showDiff && activeDetail.data ? (
              <div className="flex-1 overflow-hidden rounded border border-slate-200">
                <DiffViewer
                  before={activeDetail.data.prompt_markdown}
                  after={promptMarkdown}
                  beforeLabel={`v${activeDetail.data.version_label} (active)`}
                  afterLabel={`v${versionLabel} (draft)`}
                />
              </div>
            ) : (
              <Textarea
                className="min-h-[16rem] flex-1 font-mono"
                value={promptMarkdown}
                onChange={(e) => setPromptMarkdown(e.target.value)}
                placeholder="# Skill instructions&#10;..."
              />
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => saveDraft.mutate()}
              disabled={saveDraft.isPending || !versionLabel || !rationale || !promptMarkdown}
            >
              {saveDraft.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              Save draft
            </Button>
            <Button
              variant="secondary"
              onClick={() => runSandbox.mutate()}
              disabled={!createdDraftId || runSandbox.isPending}
            >
              Run sandbox
            </Button>
            <Button
              variant="primary"
              onClick={() => promote.mutate()}
              disabled={!createdDraftId || promote.isPending}
            >
              Promote to active
            </Button>
            <Button asChild variant="ghost">
              <Link to="/studio/skills">Back to library</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
